import { useState } from "react";
import { Printer, MapPin, CheckCircle2 } from "lucide-react";
import { REMOVAL_WINDOW, humanSlot } from "@/data/collections";
import { StatusPill, DepositPill } from "@/components/AdminDetails";

export const PriorityRemovals = ({ bookings, onSelect, onToggle }) => {
  const [showDone, setShowDone] = useState(false);
  const all = bookings.filter((b) => b.priority_removal && b.status !== "denied");
  const doneCount = all.filter((b) => b.removal_completed).length;
  const list = all.filter((b) => showDone || !b.removal_completed).sort((a, b) => (a.removal_date || "9").localeCompare(b.removal_date || "9") || a.date.localeCompare(b.date));
  const groups = Object.entries(list.reduce((m, b) => { (m[b.removal_date || "none"] ||= []).push(b); return m; }, {})).sort(([a], [b]) => (a === "none" ? 1 : b === "none" ? -1 : a.localeCompare(b)));
  const fmtDay = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="border" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }} data-testid="admin-priority-view">
      <div className="p-5 border-b flex flex-wrap items-start justify-between gap-4" style={{ borderColor: "var(--pp-border)" }}>
        <div>
          <div className="pp-caption mb-1">Priority Removal · $75 upgrade</div>
          <h2 className="font-serif-display text-3xl" style={{ color: "var(--pp-burgundy)" }} data-testid="admin-priority-title">{list.length} customer{list.length === 1 ? "" : "s"} booked</h2>
          <div className="text-xs mt-1" style={{ color: "var(--pp-text-muted)" }}>Removals run {REMOVAL_WINDOW.label}. Sorted by preferred removal date.</div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest cursor-pointer" style={{ color: "var(--pp-text-muted)" }}>
            <input type="checkbox" data-testid="admin-removal-show-done" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Show done ({doneCount})
          </label>
        <a href="/admin/print-removals" target="_blank" rel="noreferrer" data-testid="admin-priority-print"
          className="h-9 px-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest hover:bg-[var(--pp-cream)]" style={{ color: "var(--pp-terracotta)" }}>
          <Printer className="h-4 w-4" /> Print list
        </a>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="p-8 text-center text-sm" style={{ color: "var(--pp-text-muted)" }} data-testid="admin-priority-empty">No one has chosen Priority Removal yet.</div>
      ) : (
        <div>
          {groups.map(([day, rows]) => (
            <div key={day} data-testid={`admin-removal-day-${day}`}>
              <div className="px-4 py-2 flex items-center justify-between gap-2 border-y" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-parchment)" }}>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--pp-burgundy)" }}>
                  {day === "none" ? "No date set" : fmtDay(day)} · {rows.length} pickup{rows.length === 1 ? "" : "s"}
                </div>
                {day !== "none" && (
                  <a href={`/admin/print-removals?date=${day}`} target="_blank" rel="noreferrer" data-testid={`admin-removal-day-print-${day}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest underline underline-offset-2" style={{ color: "var(--pp-terracotta)" }}>
                    <Printer className="h-3 w-3" /> Print day
                  </a>
                )}
              </div>
        <ul className="divide-y" style={{ borderColor: "var(--pp-border)" }}>
          {rows.map((b) => (
            <li key={b.id} className="p-4 hover:bg-[var(--pp-cream)] transition-colors" data-testid={`admin-priority-row-${b.reference}`}>
              <button onClick={() => onSelect(b)} className="w-full text-left">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-medium" style={{ color: "var(--pp-text)" }}>{b.full_name} <span className="text-xs ml-1" style={{ color: "var(--pp-text-muted)" }}>{b.reference}</span></div>
                  <div className="flex items-center gap-1.5">
                    <span data-testid={`admin-priority-date-${b.reference}`} className="inline-flex items-center px-2.5 py-0.5 text-[10px] uppercase tracking-widest" style={{ backgroundColor: b.removal_date ? "var(--pp-burgundy)" : "#EED6D2", color: b.removal_date ? "var(--pp-parchment)" : "#8A2E2E" }}>
                      {b.removal_date ? `Remove ${b.removal_date}` : "No date set"}
                    </span>
                    <DepositPill paid={b.deposit_paid} /><StatusPill status={b.status} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 mt-2 text-xs" style={{ color: "var(--pp-text-muted)" }}>
                  <div>{b.address}</div>
                  <div>{b.phone} · {b.email}</div>
                  <div>Installed {b.date} · {humanSlot(b.time_slot)}</div>
                  <div>{b.collection_name} · {b.size_label}</div>
                  {b.customer_notes && <div className="sm:col-span-2 italic">“{b.customer_notes}”</div>}
                </div>
              </button>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address)}`} target="_blank" rel="noreferrer"
                data-testid={`admin-priority-map-${b.reference}`} className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest underline underline-offset-2" style={{ color: "var(--pp-terracotta)" }}>
                <MapPin className="h-3 w-3" /> Open in Maps
              </a>
              <button type="button" onClick={() => onToggle(b.id, { removal_completed: !b.removal_completed })} data-testid={`admin-removal-done-${b.reference}`}
                className="mt-1 ml-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest underline underline-offset-2" style={{ color: b.removal_completed ? "var(--pp-text-muted)" : "var(--pp-burgundy)" }}>
                <CheckCircle2 className="h-3 w-3" /> {b.removal_completed ? "Reopen" : "Removal done"}
              </button>
            </li>
          ))}
        </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
